import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTypedTranslation } from '@app/hooks';
import PhoneAndroidIcon from '@material-ui/icons/PhoneAndroid';
import VerifiedUser from '@material-ui/icons/VerifiedUser';
import { ModalActionsComponent, PhoneNumberInputComponent, PasslyMaskedInput, PasslyTextField } from '@app/components';
import { getModalSwitch } from '@app/store/actions';
import { myProfileService } from '@app/services';
import { digitRegex, onPressEnterModal } from '@app/helpers';
import { TranslationsTypes } from '@app/models';

export interface IReceivedData {
    handleNewPhoneNumber: Function;
}

export default function ChangePhoneDialog(props: IReceivedData) {
    const dispatch = useDispatch();
    const { translation } = useTypedTranslation();

    enum steps {
        STEP1,
        STEP2,
        STEP3
    }

    const [errorMessage, setErrorMessage] = useState('');
    const [stepOn, setStepOn] = useState(steps.STEP1);
    const [primaryBtnText, setPrimaryBtnText] = useState<TranslationsTypes>('VALIDATE_PHONE');
    const [primaryText, setPrimaryText] = useState<TranslationsTypes>('ENTER_MOBILE_PHONE');
    const [countryCodeSelected, setCountryCodeSelected] = useState('United States|+1');
    const [mobilePhoneChosen, setMobilePhoneChosen] = useState('');
    const [disablePrimaryBtn, setDisablePrimaryBtn] = useState(true);
    const [verificationCode, setVerificationCode] = useState('');

    const getPhoneNumberEnteredFromComponent = (countryCode: string, phoneNumber: string) => {
        if (countryCode !== '') {
            countryCode = countryCode.split('|')[1];
        }

        setCountryCodeSelected(countryCode);
        setMobilePhoneChosen(phoneNumber);

        const isVerifyDisabled: boolean = isVerifyPhoneButtonDisabled(countryCode, phoneNumber);
        setDisablePrimaryBtn(isVerifyDisabled);
    };

    function isVerifyPhoneButtonDisabled(countryCode: string, phoneNumber: string) {
        if (countryCode === '+1' && (!phoneNumber || phoneNumber.length < 9)) {
            return true;
        } else if (!phoneNumber || phoneNumber.length < 4) {
            return true;
        }

        return false;
    }

    const handleSubmitBtn = () => {
        let postObj: any = {};

        if (stepOn === steps.STEP1) {
            postObj = {
                phoneNumber: mobilePhoneChosen,
                countryCallingCode: countryCodeSelected
            };

            myProfileService.checkPhone(postObj, handleError, handleSuccessVerify);
        } else if (stepOn === steps.STEP2) {
            postObj = {
                verificationCode: verificationCode
            };

            myProfileService.checkPhoneProof(postObj, handleError, handleSuccessProve);
        } else if (stepOn === steps.STEP3) {
            dispatch(getModalSwitch(false));
            props.handleNewPhoneNumber(countryCodeSelected + mobilePhoneChosen);
        }
    };

    const handleSuccessVerify = (result: any) => {
        setStepOn(steps.STEP2);
        setPrimaryBtnText('PROVE_IT');
        setPrimaryText('ENTER_VERIFICATION_CODE');
        setDisablePrimaryBtn(true);
        setErrorMessage('');
    };

    const handleSuccessProve = (result: any) => {
        setStepOn(steps.STEP3);
        setPrimaryBtnText('MOBILE_PHONE_CHANGE_SUCCESS_BUTTON');
        setPrimaryText('MOBILE_PHONE_CHANGE_SUCCESS');
        setDisablePrimaryBtn(false);
        setErrorMessage('');
    };

    const handleError = (error: any) => {
        if (error.response.data.error === 'ratelimit') {
            setErrorMessage(translation('RATE_LIMIT_ERROR'));
        } else {
            setErrorMessage(error.response.data.message);                    
        }
    };

    const handleCancel = () => {
        dispatch(getModalSwitch(false));
    };

    const onFormChange = (e: any) => {
        let { name, value } = e.target;

        value = value.trim();

        switch (name) {
            case 'verificationCode':
                setVerificationCode(value);

                if (value !== '') {
                    setDisablePrimaryBtn(false);
                } else {
                    setDisablePrimaryBtn(true);
                }
                break;
            default:
                break;
        }
    };

    return (
        <>
            <div className='modal__form' tabIndex={0} onKeyPress={onPressEnterModal(handleSubmitBtn, disablePrimaryBtn)}>
                <div className='modal__dialog--content'>
                    {stepOn !== steps.STEP3 && (
                        <div className='modal__dialog--icon'>
                            <PhoneAndroidIcon />
                        </div>
                    )}

                    <div className='modal__body--inner_row'>{translation(primaryText)}</div>

                    <div>
                        {stepOn === steps.STEP1 && <PhoneNumberInputComponent handleSubmitValues={getPhoneNumberEnteredFromComponent} />}

                        {stepOn === steps.STEP2 && (
                            <PasslyTextField
                                fullWidth
                                name='verificationCode'
                                label=''
                                value={verificationCode}
                                onChange={onFormChange}
                                id='verificationCode-input'
                                InputProps={{
                                    inputComponent: PasslyMaskedInput,
                                    inputProps: {
                                        maskSize: 15,
                                        maskRegex: digitRegex
                                    }
                                }}
                                required
                                autoFocus
                            />
                        )}

                        {stepOn === steps.STEP3 && (
                            <div className='modal__dialog--icon_success'>
                                <VerifiedUser />
                            </div>
                        )}
                    </div>

                    {errorMessage && <div className='modal__error'>{errorMessage}</div>}
                </div>
            </div>
            <ModalActionsComponent
                primaryBtn={translation(primaryBtnText)}
                disabled={disablePrimaryBtn}
                secondaryBtn={stepOn !== steps.STEP3 ? translation('CANCEL') : ''}
                onClickPrimary={handleSubmitBtn}
                onClickSecondary={handleCancel}
                id='changePhone'
            />
        </>
    );
}
